import { CreditCard, RotateCw } from 'lucide-react'
import { Button } from '@highlight/ui'
import { useCheckout } from '../../../../hooks/useCheckout'
import { usePendingMessage, useRetryPendingMessage } from '../../../../store'
import { usePaywallState } from '../../../../store/paywalls'
import type { ChatMessage } from '../../../../types'
import { resolveCardStepUpCheckout } from '../../../../utils/billingUtils'

/**
 * The turn the backend held back until the account adds a card. Shown as an ordinary billing step,
 * without the error styling or the report and support buttons a failed turn gets.
 */
export function MessageCardRequired({ message, explanation }: { message: ChatMessage; explanation: string }) {
  const route = resolveCardStepUpCheckout(usePaywallState().plan)
  const keepTrial = useCheckout()
  const addCard = useCheckout('setup')
  const checkout = route.mode === 'subscription' ? keepTrial : addCard
  const pendingMessage = usePendingMessage()
  const retryPendingMessage = useRetryPendingMessage()

  const openCheckout = () => {
    if (route.mode === 'subscription') {
      keepTrial.mutate({ plan: route.tier, trial: true })
    } else {
      addCard.mutate(undefined)
    }
  }

  return (
    <div className="mt-2 px-5">
      <div className="bg-background-raised mb-3 flex w-full flex-col gap-2 rounded-2xl border p-4">
        <p className="text-foreground m-0 text-sm font-semibold leading-none">Add a card to continue</p>
        <p className="text-foreground-secondary m-0 text-sm">{explanation}</p>
        {checkout.error && <p className="text-danger-subtle m-0 text-sm">{checkout.error.message}</p>}
        <div className="mt-2 flex w-fit items-center gap-2">
          <Button size="sm" onClick={openCheckout} disabled={checkout.isPending}>
            <CreditCard size={16} />
            Add a card
          </Button>
          {message.id === pendingMessage?.id && (
            <Button size="sm" variant="secondary" onClick={() => retryPendingMessage()}>
              <RotateCw size={16} />
              Try again
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
